import React, { useState } from 'react'
import { Button } from '@resiliatech/birdsong'
import Pagination from '@mui/material/Pagination'
import { Box, Divider } from '@mui/material'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import { useMutation } from '@apollo/client'
import { GOALS } from 'Apollo/Queries/Goals'
import UPSERT_GOAL_UPDATE from 'Apollo/Mutations/UpsertGoalUpdate'
import DELETE_GOAL_UPDATE from 'Apollo/Mutations/DeleteGoalUpdate'
import { useAppState } from 'Context/AppContext/AppContext'
import { GoalUpdate } from 'Generated/graphql-types'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { Comment } from '../Comment/Comment'
import {
  Cards,
  CommentListContainer,
  Header,
  HeaderItems,
  PaginationContainer,
} from './CommentList.styled'
import { GoalCommentModal } from '../../GoalsDashboard/GoalCommentModal/GoalCommentModal'
import ConfirmActionDialogSong from '../../../../SongComponents/ConfirmActionDialogSong/ConfirmActionDialogSong'
import TitleWithTooltipSong from '../../../../SongComponents/TitleWithTooltipSong/TitleWithTooltipSong'
import { GoalSectionEmptyState } from '../EmptyStates/GoalSectionEmptyState'

type CommentList = {
  comments: GoalUpdate[]
  goalId: number
  isGoalSharedWithFunder: boolean
}

const MAX_COMMENTS_PER_PAGE = 5

export const CommentList = ({
  comments,
  goalId,
  isGoalSharedWithFunder,
}: CommentList) => {
  const { nonprofitOrgConfig: { orgId: nonprofitOrgId } = {} } = useAppState()
  const [activeComment, setActiveComment] = useState<GoalUpdate>()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [page, setPage] = useState(1)

  const [upsertGoalUpdate] = useMutation(UPSERT_GOAL_UPDATE, {
    refetchQueries: [GOALS],
  })

  const [deleteGoalUpdate] = useMutation(DELETE_GOAL_UPDATE, {
    refetchQueries: [GOALS],
  })

  const handleConfirmDelete = (comment: GoalUpdate) => {
    setActiveComment(comment)
    setIsConfirmingDelete(true)
  }

  const handleDeleteComment = async (commentId: number) => {
    deleteGoalUpdate({
      variables: { goalUpdateId: commentId, nonprofitOrgId },
    })
    setIsConfirmingDelete(false)
    setActiveComment(undefined)
  }

  const handleEditComment = (comment: GoalUpdate) => {
    setActiveComment(comment)
    setIsCommentModalOpen(true)
  }

  const dispatchQualitativeUpdateClicked = analytics.trackActionCreator(
    initiatives.Add_Qualitative_Update_Clicked
  )

  const dispatchQualitativeUpdateSubmitted = () => {
    analytics.trackActionCreator(goals.Add_Qualitative_Update_Submitted)({
      goalUpdateId: activeComment?.id,
      isGoalSharedWithFunder,
    })
  }

  const handleUpdateGoalComment = async (description: string) => {
    upsertGoalUpdate({
      variables: {
        goalUpdateInfo: {
          description,
          goalUpdateId: activeComment?.id,
          goalId,
          reportedAt: new Date().toISOString(),
        },
        nonprofitOrgId,
      },
    })
    setIsCommentModalOpen(false)
    setActiveComment(undefined)
    dispatchQualitativeUpdateSubmitted()
    // If creating a new comment, reset the page to 1
    if (!activeComment) setPage(1)
  }

  const pageCount = Math.ceil(comments.length / MAX_COMMENTS_PER_PAGE)

  const paginatedComments = comments.slice(
    (page - 1) * MAX_COMMENTS_PER_PAGE,
    page * MAX_COMMENTS_PER_PAGE
  )

  const hasComments = comments.length > 0

  const commentCards = paginatedComments.map((comment, i) => (
    <React.Fragment key={comment.id}>
      <Comment
        comment={comment}
        onDeleteComment={() => handleConfirmDelete(comment)}
        onEditComment={() => handleEditComment(comment)}
      />
      {paginatedComments.length - 1 !== i && (
        <Box padding='0 6px'>
          <Divider />
        </Box>
      )}
    </React.Fragment>
  ))

  const handleAddComment = () => {
    dispatchQualitativeUpdateClicked({})
    setActiveComment(undefined)
    setIsCommentModalOpen(true)
  }

  return (
    <CommentListContainer>
      <HeaderItems>
        <Header>Comments</Header>
        <Button
          label='Add Comment'
          variant='primary'
          onClick={handleAddComment}
        />
      </HeaderItems>
      {!hasComments && (
        <GoalSectionEmptyState
          icon={<ChatBubbleOutlineOutlinedIcon />}
          message='Add a comment to provide context or make an update on this goal'
        />
      )}
      {hasComments && (
        <Cards>
          {commentCards}
          {pageCount > 1 && (
            <PaginationContainer>
              <Pagination
                count={pageCount}
                onChange={(_, value) => setPage(value)}
                page={page}
              />
            </PaginationContainer>
          )}
        </Cards>
      )}
      {isCommentModalOpen && (
        <GoalCommentModal
          comment={activeComment}
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          onUpdateGoalComment={handleUpdateGoalComment}
        />
      )}
      {isConfirmingDelete && activeComment && (
        <ConfirmActionDialogSong
          title='Delete Comment'
          open={isConfirmingDelete}
          submitButtonText='Delete'
          onCancelHandler={() => setIsConfirmingDelete(false)}
          onSubmitHandler={() => handleDeleteComment(activeComment.id)}
        >
          <TitleWithTooltipSong title='Are you sure you want to delete this comment?' />
        </ConfirmActionDialogSong>
      )}
    </CommentListContainer>
  )
}
